import {
  podeGerirGrupo,
  TIPOS_AVATAR_ACEITOS,
  type CanalDetalhe,
  type MensagemCanalChat,
  type ParticipanteCanal,
} from '@mymaxx/shared';
import { Aviso, Botao, VisualizadorMidia } from '@mymaxx/ui';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  BellOff,
  Camera,
  Download,
  Hash,
  LogOut,
  MoreVertical,
  Pin,
  Shield,
  ShieldAlert,
  Star,
  UserMinus,
  UserPlus,
  X,
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { ErroApi } from '../lib/api';
import { internoApi } from '../lib/interno-api';
import { useSessao } from '../lib/sessao';

/**
 * O painel da conversa: quem é, o que já passou por aqui e o que dá para fazer.
 *
 * Abre ao clicar no nome, no alto do fio — o gesto que todo mundo já tem no dedo de outros
 * mensageiros. Junta num lugar só o que antes estava espalhado (participantes, palavras
 * barradas, exportar) e o que não existia: a galeria do que foi enviado, as mensagens que
 * a pessoa guardou e os grupos em comum.
 *
 * Painel lateral, e não página: quem abre isto está no meio de uma conversa e volta para
 * ela em seguida; trocar de tela perderia o lugar no fio.
 */
export function PainelDaConversa({
  canal,
  onFechar,
  onMudou,
  onIrPara,
  podeGerir,
}: {
  canal: CanalDetalhe;
  onFechar: () => void;
  onMudou: () => void;
  onIrPara: (mensagemId: string) => void;
  podeGerir: boolean;
}) {
  const [erro, setErro] = useState<string | null>(null);
  const [, definirParametros] = useSearchParams();

  /**
   * Leva para a conversa de outro canal sem sair da tela.
   *
   * Troca o `?canal=` da própria página, que é como a lista da esquerda abre qualquer
   * conversa — assim o painel não precisa receber mais uma função de fora, e o botão
   * Voltar do navegador continua funcionando.
   */
  const irParaOCanal = (id: string) => definirParametros({ canal: id });
  const [adicionando, setAdicionando] = useState(false);
  const detalhes = useQuery({
    queryKey: ['interno-detalhes', canal.id],
    queryFn: () => internoApi.detalhes(canal.id),
  });

  const presentes = canal.membros.filter((m) => !m.saiu);
  const fazer = (acao: () => Promise<unknown>) => () => {
    void acao()
      .then(onMudou)
      .catch((e: unknown) =>
        setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui fazer isso'),
      );
  };

  return (
    <aside className="flex w-80 shrink-0 flex-col border-l border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex items-center justify-between border-b border-neutral-200 px-3 py-2 dark:border-neutral-800">
        <p className="text-sm font-medium">Dados da conversa</p>
        <button
          type="button"
          aria-label="Fechar"
          onClick={onFechar}
          className="rounded p-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          <X size={16} />
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {erro && <Aviso tipo="erro">{erro}</Aviso>}

        {/* Quem é */}
        <div className="text-center">
          <RetratoGrande
            pessoa={canal.comQuem}
            grupo={canal.tipo === 'grupo'}
            fotoDoGrupo={canal.avatarUrl ?? null}
            podeTrocar={canal.tipo === 'grupo' && !canal.doSetor && podeGerirGrupo(canal.meuPapel)}
            canalId={canal.id}
            onMudou={onMudou}
            onErro={setErro}
          />
          <NomeDoGrupo canal={canal} onMudou={onMudou} onErro={setErro} />
          <p className="text-xs text-neutral-500">
            {canal.tipo === 'grupo'
              ? `${presentes.length} participante(s)${canal.doSetor ? ' · grupo do setor' : ''}`
              : canal.comQuem
                ? situacaoCurta(canal.comQuem)
                : 'Conversa direta'}
          </p>
          {canal.descricao && <p className="mt-1 text-xs text-neutral-500">{canal.descricao}</p>}
        </div>

        {/* O que dá para fazer */}
        <div className="grid grid-cols-3 gap-1 text-[11px]">
          <AcaoRapida
            ativa={canal.fixado}
            icone={<Pin size={16} />}
            rotulo={canal.fixado ? 'Fixada' : 'Fixar'}
            onClick={fazer(() => internoApi.fixar(canal.id, !canal.fixado))}
          />
          <AcaoRapida
            ativa={canal.silenciado}
            icone={canal.silenciado ? <BellOff size={16} /> : <Bell size={16} />}
            rotulo={canal.silenciado ? 'Silenciada' : 'Silenciar'}
            onClick={fazer(() =>
              canal.silenciado
                ? internoApi.tirarSilencio(canal.id)
                : internoApi.silenciar(canal.id, null),
            )}
          />
          <AcaoRapida
            icone={<Download size={16} />}
            rotulo="Exportar"
            onClick={() => {
              void internoApi
                .baixarHistorico(canal.id, canal.nome)
                .catch(() => setErro('Não consegui baixar o histórico desta conversa.'));
            }}
          />
        </div>

        {/* O que já passou por aqui */}
        <Secao titulo={`Mídia (${detalhes.data?.midias.length ?? 0})`}>
          {detalhes.isPending && <p className="text-xs text-neutral-500">Carregando…</p>}
          {detalhes.data?.midias.length === 0 && (
            <p className="text-xs text-neutral-500">Nada enviado nesta conversa ainda.</p>
          )}
          <div className="grid grid-cols-3 gap-1">
            {(detalhes.data?.midias ?? []).map((m) => (
              <button
                key={m.id}
                type="button"
                title={`Ir até a mensagem de ${m.autor?.nome ?? 'alguém'}`}
                onClick={() => onIrPara(m.id)}
                className="aspect-square overflow-hidden rounded border border-neutral-200 dark:border-neutral-700"
              >
                {m.midia?.mimeType.startsWith('image/') ? (
                  <img src={m.midia.url} alt="" className="size-full object-cover" />
                ) : (
                  <span className="flex size-full items-center justify-center p-1 text-[9px] break-all text-neutral-500">
                    {m.midia?.nomeOriginal.slice(0, 18) ?? 'arquivo'}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Secao>

        <Secao titulo={`Mensagens guardadas (${detalhes.data?.guardadas.length ?? 0})`}>
          {detalhes.data?.guardadas.length === 0 && (
            <p className="text-xs text-neutral-500">
              Nenhuma guardada. Use a estrela na mensagem para marcar o que você vai precisar achar
              depois.
            </p>
          )}
          <ul className="space-y-1">
            {(detalhes.data?.guardadas ?? []).map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => onIrPara(m.id)}
                  className="w-full rounded border border-neutral-200 px-2 py-1 text-left text-xs hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-900"
                >
                  <span className="block truncate font-medium">{m.autor?.nome ?? 'Alguém'}</span>
                  <span className="block truncate text-neutral-500">
                    {m.texto?.trim() || (m.midia ? m.midia.nomeOriginal : '—')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Secao>

        {canal.tipo === 'direto' && (
          <Secao titulo={`Grupos em comum (${detalhes.data?.gruposEmComum.length ?? 0})`}>
            {detalhes.data?.gruposEmComum.length === 0 && (
              <p className="text-xs text-neutral-500">Nenhum grupo com esta pessoa.</p>
            )}
            <ul className="space-y-1">
              {(detalhes.data?.gruposEmComum ?? []).map((g) => (
                <li key={g.id}>
                  {/* O grupo é um lugar para onde ir, e não um rótulo: quem está vendo
                      "temos este grupo em comum" quase sempre quer abri-lo. */}
                  <button
                    type="button"
                    onClick={() => irParaOCanal(g.id)}
                    className="flex w-full items-center gap-2 rounded px-1 py-1 text-left text-xs hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  >
                    {g.avatarUrl ? (
                      <img
                        src={g.avatarUrl}
                        alt=""
                        className="size-6 shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-neutral-200 dark:bg-neutral-700">
                        <Hash size={12} className="text-neutral-500" />
                      </span>
                    )}
                    <span className="truncate">{g.nome}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Secao>
        )}

        {canal.tipo === 'grupo' && (
          <Secao titulo={`Participantes (${presentes.length})`}>
            <ul className="space-y-1">
              {presentes.map((m) => (
                <Participante
                  key={`${m.tipo}:${m.id}`}
                  membro={m}
                  canal={canal}
                  onMudou={onMudou}
                  onErro={setErro}
                />
              ))}
            </ul>

            {/* No grupo do setor, quem entra e sai é o cadastro da equipe. */}
            {!canal.doSetor && podeGerirGrupo(canal.meuPapel) && (
              <Botao variante="secundario" onClick={() => setAdicionando(true)}>
                <UserPlus size={14} /> Adicionar
              </Botao>
            )}
            {!canal.doSetor && (
              <Botao variante="perigo" onClick={fazer(() => internoApi.sair(canal.id))}>
                <LogOut size={14} />{' '}
                {canal.meuPapel === 'dono' ? 'Sair e passar a posse' : 'Sair da conversa'}
              </Botao>
            )}
            {canal.doSetor && (
              <p className="rounded bg-neutral-100 p-2 text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                Grupo do setor: quem está na equipe participa automaticamente, e quem sai do setor
                sai daqui.
              </p>
            )}

            {podeGerir && <PalavrasBarradas canal={canal} onMudou={onMudou} />}
          </Secao>
        )}
      </div>

      {adicionando && (
        <EscolherParaOGrupo
          canal={canal}
          onFechar={() => setAdicionando(false)}
          onPronto={() => {
            setAdicionando(false);
            onMudou();
          }}
        />
      )}
    </aside>
  );
}

/** "Disponível" ou "visto por último": a mesma informação do cabeçalho do fio. */
function situacaoCurta(pessoa: ParticipanteCanal): string {
  if (pessoa.online) return pessoa.presenca === 'pausa' ? 'Em pausa' : 'Disponível';
  if (!pessoa.vistoEm) return 'Nunca entrou';
  const minutos = Math.floor((Date.now() - new Date(pessoa.vistoEm).getTime()) / 60_000);
  if (minutos < 2) return 'Visto agora há pouco';
  if (minutos < 60) return `Visto há ${minutos} min`;
  const quando = new Date(pessoa.vistoEm);
  return `Visto em ${quando.toLocaleDateString('pt-BR')} às ${quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="space-y-2 border-t border-neutral-200 pt-3 dark:border-neutral-800">
      <p className="text-[11px] font-medium tracking-wide text-neutral-500 uppercase">{titulo}</p>
      {children}
    </div>
  );
}

function AcaoRapida({
  icone,
  rotulo,
  ativa,
  onClick,
}: {
  icone: ReactNode;
  rotulo: string;
  ativa?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 transition ${
        ativa
          ? 'border-marca-400 bg-marca-50 text-marca-800 dark:border-marca-700 dark:bg-marca-950 dark:text-marca-100'
          : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'
      }`}
    >
      {icone}
      {rotulo}
    </button>
  );
}

/**
 * Uma linha da lista de participantes: nome, papel e o que o dono pode fazer com ela.
 *
 * As ações ficam atrás de um menu em vez de botões soltos: numa lista de vinte pessoas,
 * dois botões por linha viram quarenta botões, e o que se procura ali é um nome.
 */
function Participante({
  membro,
  canal,
  onMudou,
  onErro,
}: {
  membro: ParticipanteCanal;
  canal: CanalDetalhe;
  onMudou: () => void;
  onErro: (erro: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const meuId = useSessao((e) => e.usuario?.id);
  const souEu = membro.tipo === 'usuario' && membro.id === meuId;

  // Só o dono mexe em gente, e nunca em si mesmo. No grupo do setor ninguém mexe: lá quem
  // manda é o cadastro da equipe.
  const podeMexer = canal.meuPapel === 'dono' && !canal.doSetor && !souEu;

  const fazer = (acao: () => Promise<unknown>) => () => {
    setAberto(false);
    void acao()
      .then(onMudou)
      .catch((e: unknown) =>
        onErro(e instanceof Error ? e.message : 'Não consegui fazer isso agora.'),
      );
  };

  const alvo = { tipo: membro.tipo, id: membro.id } as const;

  return (
    <li className="relative flex items-center gap-2 rounded border border-neutral-200 px-2 py-1 text-xs dark:border-neutral-700">
      <span className="min-w-0 flex-1 truncate">{membro.nome}</span>
      <span
        className={`shrink-0 ${
          membro.papel === 'dono'
            ? 'font-medium text-amber-600 dark:text-amber-400'
            : membro.papel === 'admin'
              ? 'font-medium text-marca-600 dark:text-marca-300'
              : 'text-neutral-500'
        }`}
      >
        {membro.papel === 'dono'
          ? 'dono'
          : membro.papel === 'admin'
            ? 'admin'
            : membro.tipo === 'tecnico'
              ? 'técnico'
              : 'equipe'}
      </span>

      {podeMexer && (
        <button
          type="button"
          aria-label={`Opções de ${membro.nome}`}
          onClick={() => setAberto((v) => !v)}
          className="shrink-0 rounded p-0.5 text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700"
        >
          <MoreVertical size={13} />
        </button>
      )}

      {aberto && (
        <div className="absolute top-full right-0 z-20 mt-1 w-48 rounded-lg border border-neutral-200 bg-white p-1 shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
          <button
            type="button"
            onClick={fazer(() =>
              internoApi.definirPapel(
                canal.id,
                alvo,
                membro.papel === 'admin' ? 'membro' : 'admin',
              ),
            )}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <Shield size={13} />
            {membro.papel === 'admin' ? 'Tirar de admin' : 'Tornar admin'}
          </button>
          <button
            type="button"
            onClick={fazer(() => internoApi.removerMembro(canal.id, alvo))}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
          >
            <UserMinus size={13} />
            Remover do grupo
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * O nome do grupo, editável no lugar onde ele aparece.
 *
 * Clicar no nome e escrever por cima é menos caminho do que abrir uma tela de edição para
 * mudar uma palavra — e aqui quase sempre é uma palavra.
 */
function NomeDoGrupo({
  canal,
  onMudou,
  onErro,
}: {
  canal: CanalDetalhe;
  onMudou: () => void;
  onErro: (erro: string) => void;
}) {
  const [editando, setEditando] = useState(false);
  const [nome, setNome] = useState(canal.nome);
  const pode = canal.tipo === 'grupo' && !canal.doSetor && podeGerirGrupo(canal.meuPapel);

  if (!editando) {
    return (
      <button
        type="button"
        disabled={!pode}
        onClick={() => {
          setNome(canal.nome);
          setEditando(true);
        }}
        className="mt-2 font-medium disabled:cursor-default"
        title={pode ? 'Clique para renomear o grupo' : undefined}
      >
        {canal.nome}
      </button>
    );
  }

  const salvar = () => {
    const limpo = nome.trim();
    setEditando(false);
    if (limpo.length < 2 || limpo === canal.nome) return;
    void internoApi
      .editarGrupo(canal.id, { nome: limpo, descricao: canal.descricao ?? null })
      .then(onMudou)
      .catch((e: unknown) =>
        onErro(e instanceof Error ? e.message : 'Não consegui renomear o grupo.'),
      );
  };

  return (
    <input
      autoFocus
      value={nome}
      maxLength={80}
      onChange={(e) => setNome(e.target.value)}
      onBlur={salvar}
      onKeyDown={(e) => {
        if (e.key === 'Enter') salvar();
        if (e.key === 'Escape') setEditando(false);
      }}
      className="mt-2 w-full rounded border border-neutral-300 px-2 py-1 text-center text-sm dark:border-neutral-600 dark:bg-neutral-800"
    />
  );
}

/**
 * A foto grande do painel, que amplia ao ser clicada.
 *
 * Mesmo gesto da foto enviada na conversa — e o mesmo visualizador, com baixar e fechar.
 * Antes só dava para abri-la numa aba nova, que era justamente o caminho que baixava o
 * arquivo em vez de mostrá-lo (08/10).
 */
function FotoQueAmplia({ url, nome }: { url: string; nome: string }) {
  const [ampliada, setAmpliada] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setAmpliada(true)}
        title="Ver a foto"
        className="mx-auto block"
      >
        <img src={url} alt="" className="size-20 rounded-full object-cover" />
      </button>
      {ampliada && (
        <VisualizadorMidia
          url={url}
          nomeOriginal={`${nome}.jpg`}
          mimeType="image/jpeg"
          aoFechar={() => setAmpliada(false)}
        />
      )}
    </>
  );
}

/** O retrato grande do topo do painel: foto, ou as iniciais, ou o losango do grupo. */
function RetratoGrande({
  pessoa,
  grupo,
  fotoDoGrupo,
  podeTrocar,
  canalId,
  onMudou,
  onErro,
}: {
  pessoa?: ParticipanteCanal | null;
  grupo: boolean;
  fotoDoGrupo?: string | null;
  podeTrocar?: boolean;
  canalId?: string;
  onMudou?: () => void;
  onErro?: (erro: string) => void;
}) {
  const campo = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);

  if (grupo) {
    const trocar = async (arquivo: File) => {
      setEnviando(true);
      try {
        await internoApi.enviarFotoDoGrupo(canalId!, arquivo);
        onMudou?.();
      } catch (e) {
        onErro?.(e instanceof Error ? e.message : 'Não consegui trocar a foto do grupo.');
      } finally {
        setEnviando(false);
      }
    };

    return (
      <div className="relative mx-auto w-20">
        {fotoDoGrupo ? (
          <FotoQueAmplia url={fotoDoGrupo} nome="Foto do grupo" />
        ) : (
          <span className="flex size-20 items-center justify-center rounded-full bg-neutral-200 text-neutral-500 dark:bg-neutral-700">
            <Hash size={28} />
          </span>
        )}
        {podeTrocar && (
          <>
            <input
              ref={campo}
              type="file"
              accept={TIPOS_AVATAR_ACEITOS.join(',')}
              className="hidden"
              onChange={(e) => {
                const arquivo = e.target.files?.[0];
                e.target.value = '';
                if (arquivo) void trocar(arquivo);
              }}
            />
            <button
              type="button"
              title={fotoDoGrupo ? 'Trocar a foto do grupo' : 'Pôr uma foto no grupo'}
              aria-label="Trocar a foto do grupo"
              disabled={enviando}
              onClick={() => campo.current?.click()}
              className="absolute right-0 bottom-0 rounded-full bg-marca-600 p-1.5 text-white shadow disabled:opacity-50"
            >
              <Camera size={13} />
            </button>
            {fotoDoGrupo && (
              <button
                type="button"
                title="Tirar a foto"
                aria-label="Tirar a foto do grupo"
                onClick={() => void internoApi.removerFotoDoGrupo(canalId!).then(onMudou)}
                className="absolute bottom-0 left-0 rounded-full bg-neutral-200 p-1.5 text-neutral-600 shadow dark:bg-neutral-700 dark:text-neutral-200"
              >
                <X size={13} />
              </button>
            )}
          </>
        )}
      </div>
    );
  }
  if (pessoa?.avatarUrl) {
    return <FotoQueAmplia url={pessoa.avatarUrl} nome={pessoa.nome ?? 'Foto de perfil'} />;
  }
  const iniciais = (pessoa?.nome ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  return (
    <span className="mx-auto flex size-20 items-center justify-center rounded-full bg-neutral-200 text-xl font-medium text-neutral-600 dark:bg-neutral-700 dark:text-neutral-200">
      {iniciais}
    </span>
  );
}

/** As palavras que não podem ser usadas no grupo. A trava é do servidor; aqui só cadastra. */
function PalavrasBarradas({ canal, onMudou }: { canal: CanalDetalhe; onMudou: () => void }) {
  const [abrindo, setAbrindo] = useState(false);
  const [texto, setTexto] = useState(canal.palavrasSensiveis.join(', '));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  if (!abrindo) {
    return (
      <button
        type="button"
        onClick={() => setAbrindo(true)}
        className="flex items-center gap-1 text-xs text-neutral-500 underline"
      >
        <ShieldAlert size={13} />
        Palavras barradas
        {canal.palavrasSensiveis.length > 0 && ` (${canal.palavrasSensiveis.length})`}
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-neutral-200 p-2 dark:border-neutral-700">
      <p className="text-[11px] text-neutral-500">
        Separadas por vírgula. Quem tentar usar recebe a recusa na hora, com o motivo — e a
        tentativa fica registrada na auditoria.
      </p>
      <textarea
        rows={2}
        value={texto}
        aria-label="Palavras barradas"
        placeholder="senha, contrato, boleto"
        onChange={(e) => setTexto(e.target.value)}
        className="w-full resize-none rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
      />
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <div className="flex gap-2">
        <Botao
          carregando={salvando}
          onClick={() => {
            setSalvando(true);
            setErro(null);
            void internoApi
              .definirPalavras(
                canal.id,
                texto
                  .split(',')
                  .map((p) => p.trim())
                  .filter((p) => p.length >= 2),
              )
              .then(() => {
                setAbrindo(false);
                onMudou();
              })
              .catch((e: unknown) =>
                setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui salvar'),
              )
              .finally(() => setSalvando(false));
          }}
        >
          Salvar
        </Botao>
        <Botao variante="secundario" onClick={() => setAbrindo(false)}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}

/** Acrescenta gente ao grupo, reaproveitando a mesma escolha da nova conversa. */
function EscolherParaOGrupo({
  canal,
  onFechar,
  onPronto,
}: {
  canal: CanalDetalhe;
  onFechar: () => void;
  onPronto: () => void;
}) {
  const [busca, setBusca] = useState('');
  const [salvando, setSalvando] = useState(false);
  const contatos = useQuery({
    queryKey: ['interno-contatos', busca],
    queryFn: () => internoApi.contatos(busca || undefined),
  });
  const dentro = new Set(canal.membros.map((m) => `${m.tipo}:${m.id}`));
  const lista = (contatos.data ?? []).filter((c) => !dentro.has(`${c.tipo}:${c.id}`));

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-white p-3 dark:bg-neutral-900">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium">Adicionar ao grupo</p>
        <button type="button" aria-label="Fechar" onClick={onFechar} className="text-neutral-500">
          <X size={16} />
        </button>
      </div>
      <input
        autoFocus
        value={busca}
        placeholder="Procurar pessoa"
        aria-label="Procurar pessoa"
        onChange={(e) => setBusca(e.target.value)}
        className="mb-2 w-full rounded-lg border border-neutral-300 px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
      />
      <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto">
        {lista.map((c) => (
          <li key={`${c.tipo}:${c.id}`}>
            <button
              type="button"
              disabled={salvando}
              onClick={() => {
                setSalvando(true);
                void internoApi
                  .adicionarMembros(canal.id, {
                    usuarioIds: c.tipo === 'usuario' ? [c.id] : [],
                    clienteIds: c.tipo === 'tecnico' ? [c.id] : [],
                  })
                  .then(onPronto)
                  .finally(() => setSalvando(false));
              }}
              className="w-full rounded border border-neutral-200 px-2 py-1 text-left text-xs hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-900"
            >
              <span className="block truncate">{c.nome}</span>
              <span className="block truncate text-neutral-500">{c.descricao}</span>
            </button>
          </li>
        ))}
        {contatos.isSuccess && lista.length === 0 && (
          <li className="py-4 text-center text-xs text-neutral-500">Ninguém novo com esse nome.</li>
        )}
      </ul>
    </div>
  );
}

/**
 * A estrela da bolha: guarda a mensagem na lista de quem clicou.
 *
 * Chama-se "guardar", e não "favoritar": favoritar passou a valer só para conversa, e uma
 * palavra servindo a dois conceitos não serve a nenhum (08/10).
 */
export function EstrelaDaMensagem({
  canalId,
  mensagem,
  onMudou,
}: {
  canalId: string;
  mensagem: MensagemCanalChat;
  onMudou: () => void;
}) {
  return (
    <button
      type="button"
      title={mensagem.guardada ? 'Tirar das guardadas' : 'Guardar mensagem'}
      aria-label={mensagem.guardada ? 'Tirar das guardadas' : 'Guardar mensagem'}
      onClick={() => void internoApi.guardar(canalId, mensagem.id).then(onMudou)}
      className="rounded p-1 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 dark:hover:bg-neutral-700 dark:hover:text-neutral-100"
    >
      <Star size={14} className={mensagem.guardada ? 'fill-amber-400 text-amber-500' : ''} />
    </button>
  );
}
